import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import CodeMirror from "@uiw/react-codemirror";
import { html as htmlLang } from "@codemirror/lang-html";
import { EditorView } from "@codemirror/view";

export function SettingsEmail() {
  const [email, setEmail] = useState<string>("");
  const [replyToEmail, setReplyToEmail] = useState<string>("");
  const [defaultTemplateHtml, setDefaultTemplateHtml] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/custom-email");

        if (response.ok) {
          const data = await response.json();
          if (data.customEmail) {
            setEmail(data.customEmail.email || "");
            setDefaultTemplateHtml(data.customEmail.defaultTemplateHtml || "");
          }
          setReplyToEmail(data.replyToEmail || "");
        } else {
          console.error("Failed to fetch custom email");
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (!email.trim()) {
        toast.error("Email is required");
        setIsSaving(false);
        return;
      }

      const response = await fetch("/api/custom-email", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          replyToEmail: replyToEmail.trim(),
          defaultTemplateHtml,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save custom email");
      }

      toast.success("Email settings saved successfully");
    } catch (error) {
      console.error("Error saving email settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save email settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDefaultTemplateHtmlChange = useCallback((value: string) => {
    setDefaultTemplateHtml(value);
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Email Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              placeholder="your-email@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reply-to-email">Reply-To Address</Label>
            <Input
              id="reply-to-email"
              type="email"
              value={replyToEmail}
              onChange={(e) => {
                setReplyToEmail(e.target.value);
              }}
              placeholder="replies@example.com"
            />
            <p className="text-xs text-muted-foreground">
              Replies to your emails will be sent to this address.
            </p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Default Email Template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="default-template-html">
            HTML used when composing a new email campaign
          </Label>
          <div className="border rounded-md overflow-hidden min-h-[240px]">
            <CodeMirror
              value={defaultTemplateHtml}
              height="240px"
              extensions={[htmlLang(), EditorView.lineWrapping]}
              onChange={handleDefaultTemplateHtmlChange}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                highlightActiveLine: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: true,
              }}
              className="text-sm"
            />
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </div>
  );
}
